import { scroll } from "@utilities";

const CLASS_PREFIX = "js-accordion";
const TAB_CONTAINER_CLASS = "js-tabs-content-container";
const ICON_ROTATE_CLASS = "rotate-180";
const BG_WHITE_CLASS = "bg-white";

const DATA_BG_CHANGE = "bgChange";
const DATA_IS_OPENED = "isOpened";

/**
 * Handle accordion behaviour based on the given container
 *
 * @param container {HTMLElement} container element
 */
const Accordion = (container: HTMLElement): void => {
  // Elements
  const buttonEl: HTMLAnchorElement = container.querySelector(`.${CLASS_PREFIX}-button`);
  const iconEl: SVGElement = container.querySelector(`.${CLASS_PREFIX}-button-icon`);
  const contentContainerEl: HTMLDivElement = container.querySelector(
    `.${CLASS_PREFIX}-content-container`
  );
  const contentEl: HTMLDivElement = container.querySelector(`.${CLASS_PREFIX}-content`);
  const isItemButtonBgChangeable = container.dataset?.[DATA_BG_CHANGE] !== undefined;
  const isOpenedOnLoad = container.dataset?.[DATA_IS_OPENED] !== undefined ? JSON.parse(container.dataset?.[DATA_IS_OPENED].toLowerCase()) : false;
  
  /* Tab ancestor helpers */
  const tabAncestor = container.closest(`.${TAB_CONTAINER_CLASS}`);
  const isNestedInTab = !!tabAncestor;

  /* Call on height toggle if nested in tab */
  const toggleHeightEvent = new Event("accordionToggle") as AccordionToggleEvent;

  let isAccordionOpen = false;
  let contentHeight = 0;

  if (!buttonEl || !contentContainerEl) return;

  const itemAnchor = `#${container.id}`;
  const href = buttonEl.getAttribute("href");

  /**
   * Apply visibility: hidden; style for accordion content in case
   * accordion is closed to prevent keyboard navigation from focusing
   * focusable elements within accordion content element.
   */
  const changeContentVisibilityCloseState = (): void => {
    if (isAccordionOpen) return;

    contentContainerEl.classList.add("invisible");
  };

  /**
   * Remove visibility: hidden; style for accordion content in case
   * accordion is open to allow keyboard navigation focus.
   */
  const changeContentVisibilityOpenState = (): void => {
    if (!isAccordionOpen) return;

    contentContainerEl.classList.remove("invisible");
  };

  const changeAriaState = (): void => {
    buttonEl.setAttribute("aria-expanded", `${isAccordionOpen}`);
    contentContainerEl.setAttribute("aria-hidden", `${!isAccordionOpen}`);
  };

  const animateOpenContentHeight = (): void => {
    contentContainerEl.style.maxHeight = `${contentHeight}px`;

    if (isNestedInTab) {
      toggleHeightEvent.additionalHeight = contentHeight;

      tabAncestor.dispatchEvent(toggleHeightEvent);
    }
  };

  const openAccordion = (): void => {
    changeAriaState();

    iconEl.classList.add(ICON_ROTATE_CLASS);

    if (isItemButtonBgChangeable) {
      buttonEl.classList.add(BG_WHITE_CLASS);
    }

    animateOpenContentHeight();
  };

  const closeAccordion = (): void => {
    changeAriaState();

    iconEl.classList.remove(ICON_ROTATE_CLASS);

    if (isItemButtonBgChangeable) {
      buttonEl.classList.remove(BG_WHITE_CLASS);
    }

    contentContainerEl.style.maxHeight = null;

    if (isNestedInTab) {
      // Remove additional height on close accordion in tab
      toggleHeightEvent.additionalHeight = -contentHeight;
      tabAncestor.dispatchEvent(toggleHeightEvent);
    }
  };

  const handleClick = (event: MouseEvent): void => {
    // Prevent scroll jump on item
    event.preventDefault();

    isAccordionOpen = !isAccordionOpen;

    if (isAccordionOpen) {
      // Set URL manually without page reload
      window.history.replaceState(null, null, href);
      openAccordion();
      return;
    }

    // Remove hash from URL on Accordion close
    window.history.replaceState(null, null, window.location.href.split("#")[0]);
    closeAccordion();
  };

  const getContentContainerHeight = (): void => {
    contentHeight = contentContainerEl.scrollHeight;

    if (isAccordionOpen) {
      animateOpenContentHeight();
    }
  };

  /**
   * Initial accordion item state is CLOSED.
   * So when URL hash is equal to item ID we can open accordion.
   *
   * @param withScroll {boolean} Decide if page should scroll on element
   * @returns void
   */
  const checkInitialStateFromUrl = (withScroll = true): void => {
    const urlHash = window.location.hash;

    if (urlHash !== itemAnchor) return;

    isAccordionOpen = true;

    openAccordion();

    if (withScroll) {
      scroll.toElement({ target: container });
    }
  };

  /**
   * Check for content resize change to prevent wrong content container height
   * calculation due to font family swap period
   */
  const resizeObserver = new ResizeObserver((): void => {
    getContentContainerHeight();
  });

  // Event listeners
  buttonEl.addEventListener("click", handleClick);
  resizeObserver.observe(contentEl);
  // Allow Accordion opening based on anchor click on page without page reload
  window.addEventListener("hashchange", () => checkInitialStateFromUrl(false));

  // Check initial open/close state based on URL
  checkInitialStateFromUrl();


  if (isOpenedOnLoad == true) {
    isAccordionOpen = true;
    openAccordion();
  }

  // Check initial content visibility attributes check
  changeContentVisibilityCloseState();
  changeContentVisibilityOpenState();

  // Remove hidden style right after accordion open transition started
  contentContainerEl.addEventListener("transitionstart", changeContentVisibilityOpenState);
  // Apply hidden style after accordion transition ended
  contentContainerEl.addEventListener("transitionend", changeContentVisibilityCloseState);
};

export default Accordion;
